import { startSupportMeasureReportPdfExport } from '@/api/resources/supportMeasureEntry.api';
import { useAnalytics } from '@/composables/analytics/useAnalytics';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { usePdfExportOperation } from '@/composables/usePdfExportOperation';
import { useToast } from '@/composables/useToast';
import { AnalyticsEvents } from '@/services/analytics/events';
import { downloadFile } from '@/utils/download.utils';
import { EIcon } from 'common/enum/misc.enum';
import type { IPdfExportResult } from 'common/interface/pdf-export-result.interface';
import type { ISupportMeasureReportOptions } from 'common/interface/support-measure-report.interface';
import { computed, ref, type ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';

interface IDownloadSupportMeasureReportsParams {
  entryId: number;
  /** Omitted for all current members of the measure. */
  studentIds?: number[];
  studentCount: number;
  options: ISupportMeasureReportOptions;
}

interface ISupportMeasureReportPdfComposable {
  loading: ComputedRef<boolean>;
  downloadReports: (params: IDownloadSupportMeasureReportsParams) => Promise<void>;
}

export const useSupportMeasureReportPdf = (): ISupportMeasureReportPdfComposable => {
  const { runPdfExport } = usePdfExportOperation();
  const { customErrorToast } = useToast();
  const { confirm } = useConfirmDialog();
  const { trackEvent } = useAnalytics();
  const { t } = useI18n();

  const loadingState = ref(false);
  const loading = computed(() => loadingState.value);

  // The download starts from the confirm click: a window opened after the polling would be blocked as a popup.
  const confirmDownload = async (result: IPdfExportResult, studentCount: number): Promise<void> => {
    const confirmed = await confirm({
      title: t('supportMeasureEntry.report.confirmTitle', studentCount),
      description: t('supportMeasureEntry.report.confirmDescription', studentCount),
      confirmButtonLabel: t('supportMeasureEntry.report.download'),
      declineButtonLabel: t('general.cancel'),
      theme: 'primary',
      size: 'lg',
      confirmButtonIcon: EIcon.DOWNLOAD,
    });
    if (!confirmed) return;

    downloadFile({ url: result.url, fileName: result.filename });
    trackEvent(AnalyticsEvents.supportMeasure.reportDownloaded);
  };

  const downloadReports = async (params: IDownloadSupportMeasureReportsParams): Promise<void> => {
    loadingState.value = true;
    let result: IPdfExportResult;
    try {
      result = await runPdfExport(() =>
        startSupportMeasureReportPdfExport({
          id: params.entryId,
          dto: { ...params.options, studentIds: params.studentIds },
        }),
      );
    } catch (error) {
      console.error('Support measure report PDF generation failed', error);
      customErrorToast(t('supportMeasureEntry.report.generationFailed'));
      return;
    } finally {
      loadingState.value = false;
    }

    await confirmDownload(result, params.studentCount);
  };

  return { loading, downloadReports };
};
